// Behavior for sections/design-package-viewer.liquid.
//
// The pages themselves are the theme's own <scroll-carousel> with <button is="prev-button|next-button"> controls
// (defined in theme.js). This file only adds what those don't cover:
//   - the page-set toggle (Kitchen / Bath): shows one set + its pager and resets it to page 1
//   - the "Page X of Y" counter, kept in sync from the carousels' `carousel:select` event
//   - the Expand modal (a <dialog-element>): mirrors the active set and page into a larger carousel, and carries the
//     page the shopper ended on back to the inline viewer when it closes

const LOCK_CLASS = 'lock';

class DesignPackageViewer {
    constructor(root) {
        this.root = root;
        this.pattern = root.dataset.pageCountPattern || '[CURRENT] / [TOTAL]';
        this.modal = root.querySelector('[data-dpv-modal]');
        this.activeSetId = root.querySelector('.dpv__set:not([hidden])')?.dataset.set ?? null;

        root.addEventListener('click', (event) => {
            const toggle = event.target.closest('[data-dpv-set]');
            if (toggle) this.showSet(toggle.dataset.dpvSet);
        });
        root.addEventListener('carousel:select', (event) => this.updateCount(event));

        if (this.modal) {
            this.modal.addEventListener('dialog:after-show', () => this.onModalShow());
            this.modal.addEventListener('dialog:after-hide', () => this.onModalHide());
            this.modal.addEventListener('click', (event) => {
                if (event.target === this.modal) this.modal.hide();
            });

            // Expand is desktop only, so an open modal must not outlive a shrink below the breakpoint (e.g.
            // rotating a tablet): close it through the normal path, which also releases the scroll lock
            window.matchMedia('(max-width: 699px)').addEventListener('change', (event) => {
                if (event.matches && this.root.isConnected) this.modal.hide();
            });
        }
    }

    inlineCarousel(setId) {
        return this.root.querySelector(`.dpv__set[data-set="${setId}"] scroll-carousel`);
    }

    modalCarousel(setId) {
        return this.root.querySelector(
            `.dpv-modal__set[data-modal-set="${setId}"] scroll-carousel`,
        );
    }

    updateCount(event) {
        const carousel = event.target;
        // The pager that controls this carousel, whichever copy (inline or modal) it belongs to
        const pager = this.root.querySelector(`.dpv__pager:has([aria-controls="${carousel.id}"])`);
        const counter = pager?.querySelector('[data-dpv-count]');
        if (!counter) return;
        counter.textContent = this.pattern
            .replace('[CURRENT]', event.detail.index + 1)
            .replace('[TOTAL]', carousel.dataset.total);
    }

    showSet(setId) {
        if (setId === this.activeSetId) return;
        this.activeSetId = setId;

        this.root
            .querySelectorAll('.dpv__set')
            .forEach((set) => (set.hidden = set.dataset.set !== setId));
        this.root
            .querySelectorAll('.dpv__bar [data-nav-for]')
            .forEach((nav) => (nav.hidden = nav.dataset.navFor !== setId));
        this.root.querySelectorAll('[data-dpv-set]').forEach((button) => {
            button.setAttribute('aria-pressed', String(button.dataset.dpvSet === setId));
        });

        // Reset to the first page, and put the pager back in its starting state
        const carousel = this.inlineCarousel(setId);
        this.goTo(carousel, 0);
    }

    goTo(carousel, index) {
        if (!carousel) return;
        carousel.select(index, { animate: false, force: true });
        const pager = this.root.querySelector(`.dpv__pager:has([aria-controls="${carousel.id}"])`);
        const total = parseInt(carousel.dataset.total, 10);
        const prev = pager?.querySelector('[is="prev-button"]');
        const next = pager?.querySelector('[is="next-button"]');
        if (prev) prev.disabled = index <= 0;
        if (next) next.disabled = index >= total - 1;
    }

    onModalShow() {
        document.documentElement.classList.add(LOCK_CLASS);
        const setId = this.activeSetId;
        this.root
            .querySelectorAll('.dpv-modal__set')
            .forEach((set) => (set.hidden = set.dataset.modalSet !== setId));
        this.goTo(this.modalCarousel(setId), this.inlineCarousel(setId)?.selectedIndex ?? 0);
    }

    onModalHide() {
        document.documentElement.classList.remove(LOCK_CLASS);
        const setId = this.activeSetId;
        this.goTo(this.inlineCarousel(setId), this.modalCarousel(setId)?.selectedIndex ?? 0);
    }
}

document.querySelectorAll('[data-design-package-viewer]').forEach((root) => {
    if (!root._designPackageViewer) root._designPackageViewer = new DesignPackageViewer(root);
});

// Re-initialise a section the theme editor re-rendered
document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-design-package-viewer]').forEach((root) => {
        if (!root._designPackageViewer) root._designPackageViewer = new DesignPackageViewer(root);
    });
});
